import type { IPropertyBag } from '@deity-io/di-data-basics';
import { Button, DataTable, Divider, FlexLayout, Icon, Modal } from '@deity-io/portal-ui';
import { BaseHelmet, ConfirmationModal, CreateEditEndpointModal, EndpointMethodBadge } from 'components';
import RequestExplorerWrapper from 'components/Endpoints/RequestExplorer/RequestExplorerWrapper';
import { APOLLO_BASE_OPTIONS, ENTITY_PATH, ENTITY_PATH_ID, toLocalDateString } from 'contracts/constants';
import { DEFAULT_PAGINATION_SIZE } from 'contracts/constants/page-title';
import { type FC, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  GetEndpointsFromWebServiceDocument,
  useDeleteEndpointMutation,
  useGetEndpointsFromWebServiceQuery
} from 'services/graphql/generated/hooks';
import { getParams } from 'utils';
import { v4 } from 'uuid';

type WebServiceEndpointsOverviewProps = {
  params: {
    versionUuid: string | undefined;
    entityId: string | undefined;
  };
  isEditable?: boolean;
  service: { uuid?: string; name?: string; schema?: IPropertyBag };
};

export const WebServiceEndpoints: FC<WebServiceEndpointsOverviewProps> = ({ params, isEditable, service }) => {
  const history = useHistory();
  const [paging, setPaging] = useState({ page: 1, pageSize: DEFAULT_PAGINATION_SIZE });
  const { pathname } = useLocation();
  const pathParams: any = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [newEndpointKey, setNewEndpointKey] = useState(v4());
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState<any>();

  const {
    loading: loadingEndpoints,
    data: endpointsData,
    refetch
  } = useGetEndpointsFromWebServiceQuery({
    variables: {
      packageVersionUuid: params.versionUuid,
      webServiceUuid: params.entityId,
      offset: (paging.page - 1) * paging.pageSize,
      limit: paging.pageSize
    },
    skip: !params.versionUuid || !params.entityId,
    ...APOLLO_BASE_OPTIONS
  });
  const totalItems = endpointsData?.getEndpointsByPackageVersion?.totalItems;
  const refetchQueries = [
    {
      query: GetEndpointsFromWebServiceDocument,
      variables: {
        packageVersionUuid: params.versionUuid,
        webServiceUuid: params.entityId,
        offset: (paging.page - 1) * paging.pageSize,
        limit: paging.pageSize
      }
    }
  ];
  const [deleteEndpoint] = useDeleteEndpointMutation({
    refetchQueries
  });

  return (
    <>
      <BaseHelmet page="WebService" />
      <FlexLayout justifyContent="end" sx={{ padding: '5px' }}>
        <Button
          variant="outlined"
          disabled={isModalOpen}
          onClick={() => setIsModalOpen(true)}
          startIcon={<Icon icon="add" />}
        >
          New Endpoint
        </Button>
      </FlexLayout>
      <Divider />
      <DataTable
        data={endpointsData?.getEndpointsByPackageVersion.data || []}
        isLoading={loadingEndpoints}
        noResultsMsg="No endpoints available"
        columns={[
          {
            key: 'name',
            name: 'ENDPOINT',
            // eslint-disable-next-line react/no-unstable-nested-components
            formatter: ({ value }) => <b>{value}</b>,
            width: 40
          },
          {
            key: 'schema',
            name: 'METHOD',
            align: 'left',
            // eslint-disable-next-line react/no-unstable-nested-components
            formatter: props => <EndpointMethodBadge schema={props.value} />
          },
          {
            key: 'schema',
            name: 'path',
            align: 'left',
            // eslint-disable-next-line react/no-unstable-nested-components
            formatter: ({ value }) => <b>{value.path || '/'}</b>
          },
          {
            key: 'modifiedAt',
            name: 'LAST MODIFIED',
            formatter: ({ value }) => toLocalDateString(value)
          },
          {
            key: 'modifiedBy',
            name: 'LAST MODIFIED BY',
            // eslint-disable-next-line react/no-unstable-nested-components
            formatter: () => <div>John Smith</div>
          },
          {
            key: 'ACTIONS',
            name: 'ACTIONS',
            align: 'left',
            isActionCell: true,
            // eslint-disable-next-line react/no-unstable-nested-components
            formatter: ({ value }) => (
              <FlexLayout>
                <Button
                  endIcon={<Icon icon="edit" />}
                  onClick={() =>
                    history.push(
                      `/packages/${pathParams.packageId}/${pathParams.versionUuid}/endpoints/${value.uuid}`,
                      {
                        previous: {
                          path: `${pathname}#endpoints`,
                          label: `Service ${service.name}`,
                          entityType: 'services'
                        }
                      }
                    )
                  }
                />

                {isEditable && (
                  <Button
                    endIcon={<Icon icon="delete" color="error" />}
                    onClick={() => {
                      setSelectedEndpoint(value);
                      setIsDeleteModalOpen(true);
                    }}
                  />
                )}
                <Button
                  endIcon={<Icon icon="playCircle" color="info" />}
                  onClick={() => {
                    setSelectedEndpoint(value);
                    setExplorerOpen(true);
                  }}
                />
              </FlexLayout>
            )
          }
        ]}
        pageSize={paging.pageSize}
        onPageSizeChange={size => {
          setPaging({ ...paging, pageSize: size });
        }}
        totalResults={totalItems || 0}
        page={paging.page}
        showPagination
        onPageChange={pageNr => {
          setPaging({ ...paging, page: pageNr });
        }}
        fullClickable
      />
      {params.versionUuid && (
        <CreateEditEndpointModal
          key={newEndpointKey}
          isOpen={isModalOpen}
          versionUuid={params.versionUuid}
          webServiceUuid={service.uuid}
          refetchQueries={refetchQueries}
          setOpenState={state => setIsModalOpen(state)}
          onPackageCreated={() => {
            setIsModalOpen(false);
            setNewEndpointKey(v4());
            setPaging({ ...paging, page: 1 });
          }}
        />
      )}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        entityType="endpoint"
        entityUuid={selectedEndpoint?.uuid}
        onConfirm={uuid => {
          if (params.versionUuid) {
            deleteEndpoint({
              variables: { packageVersionUuid: params.versionUuid, uuid: uuid! }
            });
          }
        }}
      />
      <Modal isOpen={explorerOpen} onClose={() => setExplorerOpen(false)} maxWidth="xl" hideFooter>
        {params.versionUuid && selectedEndpoint && (
          <RequestExplorerWrapper
            packageVersionUuid={params.versionUuid}
            endpoint={selectedEndpoint.schema}
            service={service?.schema}
          />
        )}
      </Modal>
    </>
  );
};
